import { useEffect, useState } from "react";

import { CircularProgress, Grid, Paper, Typography } from "@mui/material";
import { getOrgConfigPublic } from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams } from "react-router-dom";

import { useDevEnvironment } from "@/contexts/DevContext";

import styles from "./CISOReportSelect.module.scss";

interface SurveyConfiguration {
  survey_id: string;
  survey_class: string;
  name: string;
  description: {
    name?: string;
    description?: string;
    image_url?: string;
  };
  is_registry: boolean;
}

const ReportCard = ({
  survey,
  onClick,
}: {
  survey: SurveyConfiguration;
  onClick: () => void;
}) => {
  return (
    <div onClick={onClick}>
      <Paper elevation={3} className={styles.reportCardContainer}>
        <Typography
          variant="h2"
          className={styles.cardTitle}
          sx={{ fontSize: "h6.fontSize", marginLeft: "4px" }}
        >
          {survey.description.image_url && (
            <img
              src={survey.description.image_url}
              alt={survey.description.name || survey.name}
              className={styles.reportImage}
            />
          )}
          {survey.description.name || survey.name}
        </Typography>
      </Paper>
    </div>
  );
};

const CISOReportSelect = () => {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const [surveys, setSurveys] = useState<SurveyConfiguration[]>([]);
  const [loading, setLoading] = useState(true);

  const { isDevEnv } = useDevEnvironment();
  const CISO_ORG_ID = isDevEnv
    ? "4b556c8c-e5fd-4e1d-93fc-81d2e5f5bea4"
    : "72568a9a-3d58-4c13-9cb2-604118ce7303";

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        const response = await getOrgConfigPublic({
          path: { org_id: CISO_ORG_ID },
        });
        const cisoSurveys =
          response.data?.surveys.filter((survey) => survey.hidden === false) ||
          [];
        // Filter out registry surveys, only show reports
        const reports = cisoSurveys.filter((survey) => !survey.is_registry);
        setSurveys(reports);
      } catch (error) {
        console.error("Error fetching CISO surveys:", error);
        toast.error("Failed to load CISO reports");
      } finally {
        setLoading(false);
      }
    };

    fetchSurveys();
  }, []);

  const handleCardClick = (surveyClass: string) => {
    navigate(`/rubiintel/${orgId}/report/new/${surveyClass}`);
  };

  return (
    <div className={styles.selectContainer}>
      <div className={styles.headerContainer}>
        <img
          src="/service-logos/CISO-Logo.png"
          alt="CISO Logo"
          className={styles.cisoLogo}
        />
        <Typography variant="h4" className={styles.title}>
          Ontario Criminal Intelligence Systems (OCIS)
        </Typography>
      </div>
      <Grid container spacing={2} className={styles.reportCardGrid}>
        {loading && (
          <Grid size={{ xs: 12 }} style={{ textAlign: "center" }}>
            <CircularProgress size={50} thickness={2} />
          </Grid>
        )}
        {!loading && surveys.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body1">
              No reports available for CISO submission.
            </Typography>
          </Grid>
        )}
        {surveys.map((survey) => (
          <Grid size={{ xs: 12, sm: 6 }} key={survey.survey_id}>
            <ReportCard
              survey={survey}
              onClick={() => handleCardClick(survey.survey_class)}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default CISOReportSelect;
